/**
 * Demand-Side Intent Capture — Sprint 17f Task 3.9
 *
 * Two-stage intent processing:
 *   1. detectMissionIntent() — mechanical regex classifier (main thread, no LLM)
 *   2. extractMissionIntent() — LLM-powered structured extraction (worker thread)
 *
 * Stage 1 decides IF the input is a mission intent. Stage 2 extracts WHAT
 * the user wants (entity, agent types, constraints, related known interest).
 */

// ---------------------------------------------------------------------------
// Intent patterns — 6 categories, need 2+ to trigger
// ---------------------------------------------------------------------------

const INTENT_PATTERNS: RegExp[] = [
  // Action verbs
  /\b(find|get|buy|book|search|look for|need|want|plan|show me|order)\b/i,
  // Domain nouns
  /\b(gift|dinner|trip|tickets|reservation|deal|flight|hotel|restaurant|shoes|clothes|gear|event)\b/i,
  // Possessive context
  /\b(for (my|a|the|our|his|her))\b/i,
  // Temporal context
  /\b(this (week|weekend|month|friday|saturday|sunday)|tonight|tomorrow|today)\b/i,
  // Budget/price context
  /\b(under \$?\d+|budget|cheap|affordable|luxury|on sale)\b/i,
  // Location context (require capitalized place name after "in" to avoid "interested in learning")
  /\b(near(by)?|around here|close to|in [A-Z]\w+)\b/,
];

const MIN_PATTERN_MATCHES = 2;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Detects whether user input is a mission creation intent.
 *
 * Returns true for actionable requests like:
 *   "find a birthday gift for my wife"
 *   "book a restaurant for Friday"
 *   "I need running shoes under $150"
 *
 * Returns false for general chat:
 *   "what's the weather"
 *   "how does the app work"
 *   "hi"
 */
export function detectMissionIntent(text: string): boolean {
  if (!text || text.length < 5) return false;

  const matchCount = INTENT_PATTERNS.filter(p => p.test(text)).length;
  return matchCount >= MIN_PATTERN_MATCHES;
}

// ---------------------------------------------------------------------------
// Structured intent extraction (LLM-powered)
// ---------------------------------------------------------------------------

const VALID_AGENT_TYPES = ['shopping', 'restaurant', 'travel', 'events', 'content'] as const;
const DEFAULT_AGENT_TYPES: ExtractedIntent['agentTypes'] = ['shopping', 'events', 'content'];

export interface ExtractedIntent {
  entityName: string;
  agentTypes: Array<'shopping' | 'restaurant' | 'travel' | 'events' | 'content'>;
  constraints: Record<string, string>;
  relatedEntity: string | null;
}

interface LLMClientLike {
  complete(userId: string, opts: {
    messages: Array<{ role: string; content: string }>;
    operation: string;
    model?: string;
    temperature?: number;
  }): Promise<{ content: string }>;
}

/**
 * Extract structured mission intent from user text using fast-tier LLM.
 * Falls back to defaults on LLM failure, malformed JSON, or null client.
 */
export async function extractMissionIntent(
  intent: string,
  knownEntityNames: string[],
  llmClient: LLMClientLike | null,
  userId: string,
  model?: string,
  onRouteLocalOutcome?: (outcome: { status: 'success' | 'parse_failed' | 'not_run'; result: 'success' | 'fallback' }) => void,
): Promise<ExtractedIntent> {
  const fallback: ExtractedIntent = {
    entityName: intent.slice(0, 200),
    agentTypes: [...DEFAULT_AGENT_TYPES],
    constraints: {},
    relatedEntity: null,
  };

  if (!llmClient) {
    onRouteLocalOutcome?.({ status: 'not_run', result: 'fallback' });
    return fallback;
  }

  let content: string;
  try {
    const response = await llmClient.complete(userId, {
      messages: [{
        role: 'user',
        content: `Extract structured mission intent. Return JSON only, no preamble.
Known user interests: ${knownEntityNames.join(', ') || 'none yet'}

User request: "${intent}"

Extract: { "entityName": "what they want (concise noun phrase)", "agentTypes": [one or more of: "shopping", "restaurant", "travel", "events", "content"], "constraints": { "budget": null or string, "timing": null or string, "location": null or string }, "relatedEntity": "matching known interest or null" }

Rules:
- entityName: concise noun phrase (e.g., "birthday gift", "Tokyo trip")
- agentTypes: only include types relevant to the request
- constraints: extract budget, timing, location if mentioned; omit null fields
- relatedEntity: match to a known interest if one is clearly related, otherwise null
- Return ONLY valid JSON`,
      }],
      ...(model ? { model } : {}),
      operation: 'mission_agent',
      temperature: 0.2,
    });
    content = response.content;
  } catch {
    onRouteLocalOutcome?.({ status: 'not_run', result: 'fallback' });
    return fallback;
  }

  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      onRouteLocalOutcome?.({ status: 'parse_failed', result: 'fallback' });
      return fallback;
    }

    const parsed = JSON.parse(jsonMatch[0]);

    // Validate and filter agent types
    const validTypes = (parsed.agentTypes ?? []).filter(
      (t: string) => (VALID_AGENT_TYPES as readonly string[]).includes(t),
    );

    // Clean constraints — remove null/undefined values
    const constraints: Record<string, string> = {};
    if (parsed.constraints && typeof parsed.constraints === 'object') {
      for (const [k, v] of Object.entries(parsed.constraints)) {
        if (v != null && typeof v === 'string' && v.length > 0) {
          constraints[k] = v;
        }
      }
    }

    const extracted = {
      entityName: typeof parsed.entityName === 'string' && parsed.entityName.length > 0
        ? parsed.entityName.slice(0, 100)
        : fallback.entityName,
      agentTypes: validTypes.length > 0 ? validTypes : [...DEFAULT_AGENT_TYPES],
      constraints,
      relatedEntity: typeof parsed.relatedEntity === 'string' && parsed.relatedEntity.length > 0
        ? parsed.relatedEntity
        : null,
    };
    onRouteLocalOutcome?.({ status: 'success', result: 'success' });
    return extracted;
  } catch {
    onRouteLocalOutcome?.({ status: 'parse_failed', result: 'fallback' });
    return fallback;
  }
}
